import { lazy, Suspense, useState, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar, TopBar } from '@/components/layout';
import { Dashboard } from '@/components/features/dashboard';
import { ErrorBoundary } from '@/components/ui';
import { OnboardingBanner } from '@/components/ui/OnboardingTooltip';
import { useMarketingHub } from '@/hooks';
import { useAuth, useToast, ListsPageProvider } from '@/contexts';
import { LoginPage } from '@/components/auth/LoginPage';
import { NotFoundPage } from '@/components/NotFoundPage';
import type { Lead, TabType } from '@/types';

// Lazy-loaded pages for code splitting
const VisitorsPage = lazy(() => import('@/components/features/visitors').then(m => ({ default: m.VisitorsPage })));
const LeadsPage = lazy(() => import('@/components/features/leads').then(m => ({ default: m.LeadsPage })));
const LeadListsView = lazy(() => import('@/components/features/lists').then(m => ({ default: m.LeadListsView })));
const ContentLibrary = lazy(() => import('@/components/features/content').then(m => ({ default: m.ContentLibrary })));
const CampaignsPage = lazy(() => import('@/components/features/campaigns').then(m => ({ default: m.CampaignsPage })));
const AdminPage = lazy(() => import('@/components/features/admin/AdminPage').then(m => ({ default: m.AdminPage })));

function PageLoader() {
  return (
    <div className="flex h-96 items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-700 border-t-[#F78C4E]" />
        <p className="text-sm text-zinc-500">Loading...</p>
      </div>
    </div>
  );
}

// Map URL paths to TabType for sidebar active state
const PATH_TO_TAB: Record<string, TabType> = {
  '/': 'dashboard',
  '/dashboard': 'dashboard',
  '/visitors': 'visitors',
  '/leads': 'leads',
  '/lists': 'lists',
  '/content': 'content',
  '/campaigns': 'campaigns',
  '/admin': 'admin',
};

const AUTH_ENABLED = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;

export default function App() {
  const { user: authUser, isLoading: authLoading, isAdmin } = useAuth();

  // Skip auth gate when Google Client ID is not configured (local dev)
  if (AUTH_ENABLED) {
    if (authLoading) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-zinc-950">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-700 border-t-[#F78C4E]" />
        </div>
      );
    }

    if (!authUser) {
      return <LoginPage />;
    }
  }

  return <AuthenticatedApp isAdmin={AUTH_ENABLED ? isAdmin : true} />;
}

function AuthenticatedApp({ isAdmin }: { isAdmin: boolean }) {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Derive activeTab from URL path
  const activeTab: TabType = PATH_TO_TAB[location.pathname] || 'dashboard';

  const {
    visitors,
    visitorsLoading,
    visitorPeriods,
    activeVisitorPeriodId,
    setActiveVisitorPeriodId,
    deleteVisitorPeriod,
    previousVisitorDomains,
    leads,
    leadsLoading,
    deals,
    dealsLoading,
    stageTransitions,
    stageTransitionsLoading,
    assets,
    assetsLoading,
    contacts,
    mondayUsers,
    leadLists,
    campaigns,
    stats,
    setSelectedAsset,
    enrichLead,
    bulkEnrichLeads,
    enrichingLeads,
    enrichingLists,
    syncingLists,
    importingVisitors,
    syncEnrichedLeadsToMonday,
    syncContactsToLeads,
    importVisitors,
    importLeads,
    createList,
    deleteList,
    markListEnriched,
    markLeadEnriched,
    addToList,
    removeFromList,
    enrichList,
    syncListToMonday,
    getListsForLead,
    getCampaignsForLead,
    createAsset,
    updateAsset,
    deleteAsset,
    importCaseStudiesFromWebsite,
    refreshData,
    lastSyncedAt,
  } = useMarketingHub({ activeTab });

  // Sequence count
  const [sequenceCount] = useState(() => {
    try {
      const seqs = localStorage.getItem('apollo_analysis_sequences');
      return seqs ? (JSON.parse(seqs) as unknown[]).length : 0;
    } catch { return 0; }
  });

  const isRefreshing = visitorsLoading || leadsLoading;

  // Navigation via URL
  const handleTabChange = (tab: TabType) => {
    navigate(tab === 'dashboard' ? '/' : `/${tab}`);
  };

  const handleNavigate = (tab: 'visitors' | 'leads' | 'campaigns' | 'lists') => {
    navigate(`/${tab}`);
  };

  // Lead/list action handlers
  const handleAddLeadsToList = (listId: string, leadIds: string[]) => {
    addToList(listId, 'leads', leadIds);
    toast.success('Added to list', `${leadIds.length} lead${leadIds.length !== 1 ? 's' : ''} added`);
  };

  const handleAddVisitorsToList = (listId: string, visitorIds: number[]) => {
    addToList(listId, 'visitors', visitorIds);
    toast.success('Added to list', `${visitorIds.length} visitor${visitorIds.length !== 1 ? 's' : ''} added`);
  };

  const handleCreateListSimple = (name: string, description: string) => {
    return createList(name, description);
  };

  const handleCreateListFull = (name: string, description: string, listType?: 'company' | 'contact') => {
    return createList(name, description, listType);
  };

  const handleDeleteList = (listId: string) => {
    deleteList(listId);
    toast.success('List deleted');
  };

  const handleEnrichLead = async (lead: Lead, domain?: string) => {
    try {
      await enrichLead(lead, domain);
      toast.success('Lead enriched', 'Apollo data has been added');
    } catch (error) {
      toast.error('Enrichment failed', error instanceof Error ? error.message : 'Unknown error');
    }
  };

  const handleBulkEnrich = async (leadIds: string[]) => {
    try {
      const result = await bulkEnrichLeads(leadIds);
      if (result.skipped === 0) {
        toast.success(`Enriched ${result.enriched} leads`, 'Apollo data has been added');
      } else {
        toast.info(`Enriched ${result.enriched} leads`, `${result.skipped} already enriched`);
      }
      return result;
    } catch (error) {
      toast.error('Bulk enrichment failed', error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  };

  const handleSyncToMonday = async (leadIds: string[], options?: { listType?: 'company' | 'contact'; listName?: string }) => {
    try {
      const result = await syncEnrichedLeadsToMonday(leadIds, options);
      if (result.failed === 0) {
        toast.success('Synced to Monday.com', `${result.updated} updated, ${result.created} created`);
      } else {
        toast.warning('Sync completed with issues', `${result.updated + result.created} synced, ${result.failed} failed`);
      }
      return result;
    } catch (error) {
      toast.error('Sync failed', error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  };

  // Sidebar props
  const apolloCredits = useMemo(() => {
    let oldestCacheAge: string | undefined;
    try {
      const stored = localStorage.getItem('apollo_enriched_companies');
      if (stored) {
        const entries = Object.values(JSON.parse(stored) as Record<string, { timestamp: number }>);
        if (entries.length > 0) {
          const oldest = Math.min(...entries.map(e => e.timestamp));
          const ageMs = Date.now() - oldest;
          const days = Math.floor(ageMs / (24 * 60 * 60 * 1000));
          if (days > 0) oldestCacheAge = `${days}d`;
          else {
            const hours = Math.floor(ageMs / (60 * 60 * 1000));
            oldestCacheAge = hours > 0 ? `${hours}h` : '<1h';
          }
        }
      }
    } catch { /* ignore */ }
    return {
      available: stats.apolloCreditsAvailable,
      cached: stats.cachedEnrichments,
      oldestCacheAge,
    };
  }, [stats.apolloCreditsAvailable, stats.cachedEnrichments]);

  const useSplitView = ['visitors', 'leads', 'lists', 'campaigns', 'contacts'].includes(activeTab);
  const showPageHeader = !useSplitView && activeTab !== 'dashboard' && activeTab !== 'admin';

  return (
    <div className="min-h-screen bg-zinc-950">
      <Sidebar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        visitorCount={visitors.length}
        leadCount={leads.length}
        listCount={leadLists.length}
        contentCount={assets.length}
        campaignCount={sequenceCount}
        contactCount={contacts.length}
        apolloCredits={apolloCredits}
      />

      <main className="ml-60 min-h-screen flex flex-col">
        <TopBar onRefresh={refreshData} isRefreshing={isRefreshing} lastSyncedAt={lastSyncedAt} />
        <div className={useSplitView ? 'flex-1 p-4' : 'flex-1 p-8'}>
          {showPageHeader && (
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-white">
                {activeTab === 'content' && 'Content Library'}
              </h1>
              <p className="mt-1 text-zinc-400">
                {activeTab === 'content' && 'Marketing assets and campaign materials'}
              </p>
            </div>
          )}

          <ErrorBoundary name={activeTab} onReset={() => refreshData({ force: true })}>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={
                  <ErrorBoundary name="dashboard" onReset={() => refreshData({ force: true })}>
                    <Dashboard
                      leads={leads} deals={deals} visitors={visitors} campaigns={campaigns}
                      leadLists={leadLists} mondayUsers={mondayUsers} stageTransitions={stageTransitions}
                      leadsLoading={leadsLoading} dealsLoading={dealsLoading}
                      visitorsLoading={visitorsLoading} stageTransitionsLoading={stageTransitionsLoading}
                      onNavigate={handleNavigate}
                    />
                  </ErrorBoundary>
                } />
                <Route path="/dashboard" element={<Navigate to="/" replace />} />
                <Route path="/visitors" element={
                  <ErrorBoundary name="visitors" onReset={() => refreshData({ force: true })}>
                    <VisitorsPage
                      visitors={visitors} leadLists={leadLists} isLoading={visitorsLoading}
                      isImporting={importingVisitors} onSyncContacts={syncContactsToLeads}
                      onAddToList={handleAddVisitorsToList} onCreateList={handleCreateListSimple}
                      onImportVisitors={importVisitors}
                      visitorPeriods={visitorPeriods}
                      activePeriodId={activeVisitorPeriodId}
                      onSelectPeriod={setActiveVisitorPeriodId}
                      onDeletePeriod={deleteVisitorPeriod}
                      previousDomains={previousVisitorDomains}
                    />
                  </ErrorBoundary>
                } />
                <Route path="/leads" element={
                  <ErrorBoundary name="leads" onReset={() => refreshData({ force: true })}>
                    <LeadsPage
                      leads={leads} deals={deals} leadLists={leadLists} campaigns={campaigns}
                      mondayUsers={mondayUsers} isLoading={leadsLoading}
                      onEnrich={handleEnrichLead} onBulkEnrich={handleBulkEnrich}
                      onSyncToMonday={handleSyncToMonday} enrichingLeads={enrichingLeads}
                      onAddToList={handleAddLeadsToList} onCreateList={handleCreateListSimple}
                      getListsForLead={getListsForLead} getCampaignsForLead={getCampaignsForLead}
                      apolloCredits={{ available: stats.apolloCreditsAvailable, cached: stats.cachedEnrichments }}
                    />
                  </ErrorBoundary>
                } />
                <Route path="/lists" element={
                  <ErrorBoundary name="lists" onReset={() => refreshData({ force: true })}>
                    <ListsPageProvider value={{
                      onAssignCampaign: undefined,
                      onCreateCampaignFromList: undefined,
                      onBulkEnrich: handleBulkEnrich,
                      onMarkListEnriched: markListEnriched,
                      onMarkLeadEnriched: markLeadEnriched,
                      onSyncLeadsToMonday: handleSyncToMonday,
                      onImportLeads: importLeads,
                      enrichingLeads,
                    }}>
                      <LeadListsView
                        lists={leadLists} leads={leads} visitors={visitors} campaigns={campaigns}
                        onCreateList={handleCreateListFull} onDeleteList={handleDeleteList}
                        onEnrichList={enrichList} onSyncToMonday={syncListToMonday}
                        onRemoveFromList={removeFromList}
                        enrichingLists={enrichingLists} syncingLists={syncingLists}
                      />
                    </ListsPageProvider>
                  </ErrorBoundary>
                } />
                <Route path="/content" element={
                  <ErrorBoundary name="content" onReset={() => refreshData({ force: true })}>
                    <ContentLibrary
                      assets={assets} isLoading={assetsLoading}
                      onViewDetails={setSelectedAsset} onCreateAsset={createAsset}
                      onUpdateAsset={updateAsset} onDeleteAsset={deleteAsset}
                      onImportFromWebsite={importCaseStudiesFromWebsite}
                    />
                  </ErrorBoundary>
                } />
                <Route path="/campaigns" element={
                  <ErrorBoundary name="campaigns" onReset={() => refreshData({ force: true })}>
                    <CampaignsPage leads={leads} leadLists={leadLists} />
                  </ErrorBoundary>
                } />
                {isAdmin && (
                  <Route path="/admin" element={<AdminPage />} />
                )}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>
      <OnboardingBanner />
    </div>
  );
}
